import { useContext, useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { DropzoneArea } from "material-ui-dropzone";
import { pdfjs } from "react-pdf";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { VehicleContext } from "../../Context/vehicleContext";
import { CacheContext } from "../../Context/cacheContext";
import { postData } from "../../Services/postData";
import { multipleDelete } from "../../Services/multipleDelete";
import { urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ImageModel } from "../../Models/Models";
import GenericModal from "../../Modal/GenericModal";
import PreviewPDF from "../Generic/PDFViewer";
import ImageCard from "./ImageCard";
import style from "../../Styles/photoGallery.module.css";
import { getActiveLocations } from "../../Utils/utils";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

export default function PhotoGallery() {
  const vehicleContext: any = useContext(VehicleContext);
  const cacheData = useContext(CacheContext)?.cache;
  const locations = getActiveLocations(cacheData?.locations);

  // State management
  const [items, setItems] = useState<any[]>([]);
  const [imageLocations, setImageLocations] = useState<Record<number, any[]>>(
    {}
  );
  const [files, setFiles] = useState<File[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number[]>([]);
  const [selectedPDF, setSelectedPDF] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Initialize images and locations from context
  useEffect(() => {
    const images = vehicleContext?.state?.images || [];
    setItems(images);

    const initialLocations: Record<number, any[]> = {};
    images.forEach((image: any) => {
      const ids = Array.isArray(image.location_ids) ? image.location_ids : [];
      // Map IDs to the actual location objects
      const matchedLocations = ids
        .map((id: number) => locations.find((loc: any) => loc.id === id))
        .filter(Boolean); // remove undefined in case some IDs don't match

      initialLocations[image.id] = matchedLocations;
    });

    setImageLocations(initialLocations);
  }, [vehicleContext?.state?.images]);

  // Drag end handler for reordering
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  // File upload handler
  function handleFileChange(uploadedFiles: File[]) {
    const renamedFiles = uploadedFiles.map((file: File) => {
      const extension = file.name.slice(file.name.lastIndexOf("."));
      const baseFileName = file.name
        .slice(0, file.name.lastIndexOf("."))
        .replace(/\s+/g, "_");
      const newFileName = baseFileName.slice(0, 150) + extension;
      return new File([file], newFileName, { type: file.type });
    });

    setFiles(renamedFiles);
  }

  // Upload files to server
  function handleUpload() {
    if (files.length === 0) return;

    const formData = new FormData();
    formData.append("vehicul_id", vehicleContext?.state?.vehicle?.id);
    formData.append("vin", vehicleContext?.state?.vehicle?.vin);
    formData.append("fileType", "Poze");

    files.forEach((file) => {
      formData.append(file.name, file);
    });

    postData(`${urlEnum.files}`, formData)
      .then((res) => {
        vehicleContext.updateVehicleImages(res.data.images);
        setShowUploadModal(false);
        setFiles([]);
      })
      .catch((_err) => {
        setShowUploadModal(false);
      });
  }

  // Toggle image selection for deletion
  function handleToggleDelete(index: number) {
    const imageId = items[index].id;
    setSelectedForDelete((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId]
    );
  }

  // Add image to site (set status to visible)
  function handleAddToSite(imageId: number) {
    const updatedItems = items.map((item) =>
      item.id === imageId ? { ...item, status_poza: "1" } : item
    );
    setItems(updatedItems);
  }

  // Remove image from site (set status to hidden)
  function handleDeleteFromSite(imageId: number) {
    const updatedItems = items.map((item) =>
      item.id === imageId ? { ...item, status_poza: "0" } : item
    );
    setItems(updatedItems);
  }

  // Make all images visible on site
  function handleMakeAllVisible() {
    const updatedItems = items.map((item) => ({
      ...item,
      status_poza: "1",
    }));
    setItems(updatedItems);
  }

  // Update location selection for an image
  function handleLocationChange(imageId: number, selectedLocations: any[]) {
    setImageLocations((prev) => ({
      ...prev,
      [imageId]: selectedLocations,
    }));
  }

  // Save all changes (order, status, locations)
  function handleSaveChanges() {
    const photoObject = Object.assign({}, new ImageModel());
    photoObject.vehicul_id = vehicleContext?.state?.vehicle?.id;
    photoObject.vin = vehicleContext?.state?.vehicle?.vin;

    items.forEach((item, index) => {
      const imageData = {
        ...item,
        sort_order: index,
        location_ids: imageLocations[item.id]?.map((loc: any) => loc.id) || [],
      };
      photoObject.infoFiles.push(imageData);
    });

    postData(`${urlEnum.multipleUpdate}`, photoObject)
      .then((res) => {
        vehicleContext.updateVehicleImages(res.data.images);
        setShowSaveModal(false);
        setIsEditMode(false);
      })
      .catch((_err) => {
        setShowSaveModal(false);
      });
  }

  // Delete selected images
  function handleDelete() {
    const itemsToDelete = items.filter((item) =>
      selectedForDelete.includes(item.id)
    );

    const data = {
      data: { infoFiles: itemsToDelete },
    };

    multipleDelete(`${urlEnum.multipleDeletePhotos}`, data)
      .then((res) => {
        vehicleContext.updateVehicleImages(res.data);
        setShowDeleteModal(false);
        setSelectedForDelete([]);
        setIsEditMode(false);
      })
      .catch((_err) => {
        setShowDeleteModal(false);
      });
  }

  // Cancel edit mode
  function handleCancel() {
    setIsEditMode(false);
    setSelectedForDelete([]);
    setItems(vehicleContext?.state?.images || []);
  }

  return (
    <div className={style.galleryContainer}>
      {/* Control Buttons */}
      <div className={style.controls}>
        {isEditMode ? (
          <>
            <Button
              variant="contained"
              onClick={() => setShowSaveModal(true)}
              style={{ backgroundColor: "#FF6633", color: "white" }}
              className={style.button}
            >
              <Typography variant="button">{Vocabulary.save}</Typography>
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedForDelete.length === 0}
              style={{ backgroundColor: "#F33", color: "white" }}
              className={style.button}
            >
              <Typography variant="button">
                {Vocabulary.deleteImages}
              </Typography>
            </Button>
            <Button
              variant="contained"
              onClick={handleMakeAllVisible}
              style={{ backgroundColor: "#0c7146", color: "white" }}
              className={style.button}
            >
              <Typography variant="button">
                {Vocabulary.setAllVisibleOnSite || "Toate vizibile pe site"}
              </Typography>
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              className={style.button}
            >
              <Typography variant="button">{Vocabulary.cancel}</Typography>
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={() => setIsEditMode(true)}
              className={style.button}
            >
              <Typography variant="button">{Vocabulary.edit}</Typography>
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowUploadModal(true)}
              className={style.button}
            >
              <Typography variant="button">{Vocabulary.addImages}</Typography>
            </Button>
          </>
        )}
      </div>

      {/* Image Grid - 8 items per row */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={rectSortingStrategy}
        >
          <div className={style.gridContainer}>
            {items.map((image, index) => (
              <ImageCard
                key={image.id}
                image={image}
                index={index}
                isEditMode={isEditMode}
                locations={locations}
                selectedLocations={imageLocations[image.id] || []}
                isSelectedForDelete={selectedForDelete.includes(image.id)}
                onToggleDelete={handleToggleDelete}
                onLocationChange={handleLocationChange}
                onAddToSite={handleAddToSite}
                onDeleteFromSite={handleDeleteFromSite}
                onPdfClick={setSelectedPDF}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* PDF Preview Modal */}
      <PreviewPDF
        title={selectedPDF?.name || ""}
        open={!!selectedPDF}
        url={`/api/${selectedPDF?.file}`}
        onClose={() => setSelectedPDF(null)}
      />

      {/* Upload Modal */}
      <GenericModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title={Vocabulary.addNewImages}
        maxWidth="80%"
      >
        <DropzoneArea
          acceptedFiles={["image/*,application/*"]}
          dropzoneClass={style.dropzoneArea}
          maxFileSize={640000000}
          filesLimit={40}
          onChange={handleFileChange}
          onDelete={() => setFiles([])}
          showAlerts={false}
        />
        <div className={style.modalActions}>
          <Button
            variant="contained"
            onClick={handleUpload}
            style={{ color: "white" }}
          >
            {Vocabulary.save}
          </Button>
        </div>
      </GenericModal>

      {/* Save Confirmation Modal */}
      <GenericModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title={Vocabulary.confirmEditPhotosStatus}
      >
        <Typography style={{ margin: 10, textAlign: "center" }}>
          {Vocabulary.confirmEditPhotos}
        </Typography>
        <Typography style={{ margin: 10, textAlign: "center" }}>
          {Vocabulary.confirmEditPhotosStatusMessage}
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 20,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setShowSaveModal(false)}
            style={{ color: "white" }}
          >
            {Vocabulary.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            style={{ color: "white" }}
          >
            {Vocabulary.save}
          </Button>
        </div>
      </GenericModal>

      {/* Delete Confirmation Modal */}
      <GenericModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title=""
      >
        <div style={{ textAlign: "center" }}>
          <Typography style={{ fontSize: 20 }}>
            {Vocabulary.confirmDeleteImages}
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              color="secondary"
              style={{ margin: 10 }}
              onClick={() => setShowDeleteModal(false)}
            >
              {Vocabulary.no}
            </Button>
            <Button
              onClick={handleDelete}
              fullWidth
              variant="contained"
              style={{ color: "white", margin: 10 }}
            >
              {Vocabulary.yes}
            </Button>
          </div>
        </div>
      </GenericModal>
    </div>
  );
}
